// src/components/HUD/HUDScriptCard.js
// ─────────────────────────────────────────────────────────────────────────────
// DEPLOYED: 2026-06-29 | Step 5 — ACL Script Run Cards with results display
// Each card fires a POST to /api/admin/hud/run-script, polls for results,
// and displays findings with color-coded severity badges.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const SCRIPTS = [
  {
    id: 'q3_unit_months',
    label: 'Q3 Unit Months',
    description: 'PHAs with HUD grants but zero unit months reported (A2023)',
    icon: '🏠',
    interpretRow: (row) => {
      const prog = (row.RT_PRGM || row.rt_prgm || '').toString().toUpperCase();
      const isYellow = ['HCV', 'EHV', 'MAINSTREAM'].some(p => prog.includes(p));
      return isYellow ? 'yellow' : 'red';
    },
  },
  {
    id: 'rpt04_delta',
    label: 'RPT04 Delta',
    description: 'Audited vs unaudited revenue variance analysis',
    icon: '📊',
    interpretRow: () => 'yellow',
  },
  {
    id: 'cfp_bookkeeping',
    label: 'CFP Bookkeeping',
    description: 'CFP bookkeeping fee violations (ITEM_91310 > 0)',
    icon: '📋',
    interpretRow: () => 'red',
  },
  {
    id: 'status_summary',
    label: 'Status Summary',
    description: 'Submission counts by status and fiscal year type',
    icon: '📈',
    interpretRow: () => 'neutral',
  },
];

const SEVERITY_STYLES = {
  red:     { bg: '#FCEBEB', border: '#D85A30', text: '#A32D2D', badge: '#FCEBEB', badgeText: '#A32D2D' },
  yellow:  { bg: '#FAEEDA', border: '#BA7517', text: '#633806', badge: '#FAEEDA', badgeText: '#633806' },
  neutral: { bg: '#EEF2FF', border: '#534AB7', text: '#3C3489', badge: '#EEF2FF', badgeText: '#3C3489' },
};

export default function HUDScriptCard({ script }) {
  const [status,  setStatus]  = useState('idle'); // idle | running | complete | error
  const [results, setResults] = useState([]);
  const [jobId,   setJobId]   = useState(null);
  const [error,   setError]   = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const cfg = SCRIPTS.find(s => s.id === script) || {
  id: script,
  label: script.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
  description: 'Custom business rule script',
  icon: '🔬',
  interpretRow: () => 'neutral',
};

  async function handleRun() {
    setStatus('running');
    setResults([]);
    setError(null);
    setElapsed(0);

    const start = Date.now();
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 500);

    try {
      // Fire the script
      // UPDATED: 2026-08-06 — /hud/run-script (requireHUDUser; admins pass through)
      const fireRes = await axios.post(
        `${API_URL}/hud/run-script`,
        { scriptName: cfg.id },
        { withCredentials: true }
      );

      const jid = fireRes.data.jobId;
      setJobId(jid);

      // Poll for results
      let attempts = 0;
      const maxAttempts = 24; // 2 min at 5s intervals

      const poll = async () => {
        attempts++;
        try {
          const res = await axios.get(
            `${API_URL}/hud/script-results/${jid}`,
            { withCredentials: true }
          );

          const { status: jobStatus, results: rows } = res.data;

          if (jobStatus === 'complete') {
            clearInterval(timer);
            setResults(rows || []);
            setStatus('complete');
          } else if (jobStatus === 'error') {
            clearInterval(timer);
            setError(res.data.error || 'Script execution failed.');
            setStatus('error');
          } else if (attempts < maxAttempts) {
            setTimeout(poll, 5000);
          } else {
            clearInterval(timer);
            setError('Timed out waiting for results.');
            setStatus('error');
          }
        } catch (pollErr) {
          clearInterval(timer);
          setError(pollErr?.response?.data?.message || pollErr.message);
          setStatus('error');
        }
      };

      setTimeout(poll, 3000);

    } catch (err) {
      clearInterval(timer);
      setError(err?.response?.data?.message || err.message);
      setStatus('error');
    }
  }

  const redCount    = results.filter(r => cfg.interpretRow(r) === 'red').length;
  const yellowCount = results.filter(r => cfg.interpretRow(r) === 'yellow').length;
  const total       = results.length;

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${status === 'complete' && total > 0 ? '#D85A30' : '#D3D1C7'}`,
      borderRadius: '10px',
      padding: '16px 18px',
      marginBottom: '12px',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1a1a18' }}>
            {cfg.icon} {cfg.label}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#888780' }}>{cfg.description}</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Result badges */}
          {status === 'complete' && (
            <>
              {redCount > 0 && (
                <span style={{
                  fontSize: '11px', fontWeight: '700', padding: '3px 10px',
                  borderRadius: '12px', background: '#FCEBEB', color: '#A32D2D',
                  border: '1px solid #D85A30',
                }}>
                  🔴 {redCount} High
                </span>
              )}
              {yellowCount > 0 && (
                <span style={{
                  fontSize: '11px', fontWeight: '700', padding: '3px 10px',
                  borderRadius: '12px', background: '#FAEEDA', color: '#633806',
                  border: '1px solid #BA7517',
                }}>
                  🟡 {yellowCount} Review
                </span>
              )}
              {total === 0 && (
                <span style={{
                  fontSize: '11px', fontWeight: '700', padding: '3px 10px',
                  borderRadius: '12px', background: '#E1F5EE', color: '#0F6E56',
                  border: '1px solid #0F6E56',
                }}>
                  ✓ No Findings
                </span>
              )}
            </>
          )}

          {/* Run button */}
          <button
            onClick={handleRun}
            disabled={status === 'running'}
            style={{
              background: status === 'running' ? '#C4C2D8' : '#534AB7',
              color: '#fff',
              border: 'none',
              borderRadius: '7px',
              padding: '7px 16px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: status === 'running' ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {status === 'running' ? (
              <>
                <span style={{
                  width: '10px', height: '10px',
                  border: '2px solid #fff', borderTopColor: 'transparent',
                  borderRadius: '50%', display: 'inline-block',
                  animation: 'aclSpin 0.7s linear infinite',
                }} />
                Running… {elapsed}s
              </>
            ) : (
              <>▶ Run ACL</>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {status === 'error' && (
        <div style={{
          padding: '8px 12px', borderRadius: '6px', fontSize: '12px',
          background: '#FCEBEB', color: '#A32D2D', border: '1px solid #D85A30',
        }}>
          {error}
        </div>
      )}

      {/* Results table */}
      {status === 'complete' && total > 0 && (
        <div style={{ marginTop: '12px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ background: '#F7F6F2' }}>
                {Object.keys(results[0]).slice(0, 6).map(col => (
                  <th key={col} style={{
                    padding: '6px 8px', textAlign: 'left', fontWeight: '600',
                    color: '#5F5E5A', borderBottom: '1px solid #E2E0D8',
                    whiteSpace: 'nowrap',
                  }}>
                    {col}
                  </th>
                ))}
                <th style={{ padding: '6px 8px', color: '#5F5E5A', borderBottom: '1px solid #E2E0D8' }}>
                  Risk
                </th>
              </tr>
            </thead>
            <tbody>
              {results.slice(0, 50).map((row, i) => {
                const sev = cfg.interpretRow(row);
                const s = SEVERITY_STYLES[sev] || SEVERITY_STYLES.neutral;
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAF8' }}>
                    {Object.values(row).slice(0, 6).map((val, j) => (
                      <td key={j} style={{
                        padding: '5px 8px', color: '#2C2C2A',
                        borderBottom: '1px solid #F0EEE8',
                      }}>
                        {val}
                      </td>
                    ))}
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #F0EEE8' }}>
                      <span style={{
                        fontSize: '10px', padding: '2px 7px', borderRadius: '10px',
                        fontWeight: '600', background: s.badge, color: s.badgeText,
                        border: `1px solid ${s.border}`,
                      }}>
                        {sev === 'red' ? 'High' : sev === 'yellow' ? 'Review' : 'Info'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > 50 && (
            <p style={{ fontSize: '11px', color: '#888780', marginTop: '6px' }}>
              Showing 50 of {total} findings
            </p>
          )}
        </div>
      )}

      <style>{`@keyframes aclSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export { SCRIPTS };